import { Smartphone, UserPlus, Share2 } from "lucide-react";

const steps = [
  {
    icon: Share2,
    number: "01",
    title: "Tap or Share",
    description: "Share your QCard via NFC tap, QR code, or send your personal link. Works with any smartphone.",
    visual: (
      <div className="relative w-full h-48 flex items-center justify-center">
        <div className="w-20 h-20 rounded-2xl gradient-primary flex items-center justify-center shadow-lg shadow-primary/30">
          <Share2 className="w-10 h-10 text-white" />
        </div>
        {/* Ripple effect */}
        <div className="absolute w-32 h-32 rounded-2xl border-2 border-primary/30 animate-ping" style={{ animationDuration: '2s' }} />
        <div className="absolute w-44 h-44 rounded-2xl border border-primary/20 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.5s' }} />
      </div>
    ),
  },
  {
    icon: UserPlus,
    number: "02",
    title: "Add to Contacts",
    description: "They tap one button and your complete profile — photo, phone, email, socials — is saved to their phone.",
    visual: (
      <div className="relative w-full h-48 flex items-center justify-center">
        <div className="w-40 h-56 bg-card border border-border rounded-3xl shadow-xl overflow-hidden">
          <div className="gradient-primary h-16 relative">
            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-card border-4 border-card flex items-center justify-center">
              <span className="text-lg font-bold text-foreground">JD</span>
            </div>
          </div>
          <div className="pt-8 px-3 text-center">
            <p className="font-semibold text-sm text-foreground">John Doe</p>
            <p className="text-xs text-muted-foreground">CEO</p>
          </div>
          <div className="px-3 py-2">
            <button className="w-full py-2 gradient-primary text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-1">
              <UserPlus className="w-3 h-3" />
              Add to Contacts
            </button>
          </div>
        </div>
      </div>
    ),
  },
  {
    icon: Smartphone,
    number: "03",
    title: "Stay Connected",
    description: "You're in their phone forever. When they need you, you're one search away — not buried in a drawer.",
    visual: (
      <div className="relative w-full h-48 flex items-center justify-center">
        <div className="w-36 h-52 bg-qcard-dark rounded-3xl p-2 shadow-xl">
          <div className="w-full h-full bg-slate-800 rounded-2xl overflow-hidden">
            <div className="px-3 py-2 bg-slate-700">
              <div className="text-xs text-white/60">Contacts</div>
              <div className="h-4 bg-slate-600 rounded mt-1 w-20" />
            </div>
            <div className="p-2 space-y-2">
              <div className="flex items-center gap-2 p-2 bg-primary/20 rounded-lg border border-primary/30">
                <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold">JD</div>
                <div>
                  <p className="text-xs font-medium text-white">John Doe</p>
                  <p className="text-[10px] text-white/60">CEO, Acme Corp</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 bg-slate-700 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-slate-600" />
                <div className="space-y-1">
                  <div className="h-2 bg-slate-600 rounded w-16" />
                  <div className="h-1.5 bg-slate-700 rounded w-12" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ),
  },
];

const HowItWorksSection = () => {
  return (
    <section className="py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
            Quick & Easy Setup
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Ready in{" "}
            <span className="text-gradient">60 Seconds</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Create your professional digital identity for free. No app downloads. No credit card. Just you, looking polished.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <div key={index} className="relative">
              {/* Connector */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-24 left-full w-full z-10">
                  <div className="flex items-center justify-center -ml-4">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    </div>
                    <div className="h-0.5 flex-1 bg-gradient-to-r from-primary/50 to-transparent" />
                  </div>
                </div>
              )}
              
              <div className="bg-card rounded-3xl border border-border p-6 hover:border-primary/30 transition-all duration-300 hover:shadow-xl">
                {/* Step Number */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-5xl font-bold text-primary/20">{step.number}</span>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <step.icon className="w-6 h-6 text-primary" />
                  </div>
                </div>
                
                {/* Visual */}
                <div className="mb-6">
                  {step.visual}
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-foreground mb-2">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
